import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { RfpLabelService } from '../../../rfp-labels.service';

import { IttTenderSubmissionGuidlinesComponent } from '../itt-tender-submission-guidlines/itt-tender-submission-guidlines.component';
import { IttContractualTermsComponent } from '../itt-contractual-terms/itt-contractual-terms.component';
import { IttKeyDatesComponent } from '../itt-key-dates/itt-key-dates.component';
import { IttWorkPackagesComponent } from '../itt-work-packages/itt-work-packages.component';
import { CompaniesSelectionComponent } from '@/app/components/projects/project-development/tendering/shared/developer/companies-selection/companies-selection.component';
import { IttTechnicalEvaluationWeightageComponent } from '../itt-technical-evaluation-weightage/itt-technical-evaluation-weightage.component';
import { IttCommercialEvaluationWeightageComponent } from '@/app/components/projects/project-development/tendering/inivitation-to-tender/itt-sender-side/itt-creation/itt-commercial-evaluation-weightage/itt-commercial-evaluation-weightage.component';
import { BriefSectionComponent } from '../../../../project-expression-of-interest/brief-section/brief-section.component';

@Component({
  selector: 'app-itt-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    IttTenderSubmissionGuidlinesComponent,
    IttContractualTermsComponent,
    IttKeyDatesComponent,
    IttWorkPackagesComponent,
    CompaniesSelectionComponent,
    IttTechnicalEvaluationWeightageComponent,
    IttCommercialEvaluationWeightageComponent,
    BriefSectionComponent,
  ],
  templateUrl: './itt-wrapper.component.html',
  styleUrl: './itt-wrapper.component.css',
})
export class IttWrapperComponent implements OnInit {
  @Output() closeModalEvent = new EventEmitter<boolean>();
  @Input() isEdit: boolean;

  currentITTStep = 1;
  ITTDeveloperData: any = {};
  projectId: number;
  projectName: string;
  ittId: number;
  labels: any;
  isRFPorRFQ: string = 'RFP'; // Flag to track RFP/RFQ mode

  private readonly totalSteps = 8;

  constructor(
    private tenderingService: TenderingService,
    private route: ActivatedRoute,
    private rfpLabelService: RfpLabelService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to route changes
    this.route.url.subscribe(() => {
      this.updateRFPorRFQFlag();
      const currentRoute = this.router.url; // Get full URL path
      this.rfpLabelService.updateLabelsFromRoute(currentRoute);
    });
    this.rfpLabelService.labels$.subscribe((labels) => {
      this.labels = labels;
    });

    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });

    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    this.tenderingService.ittId$.subscribe((ittId) => {
      this.ittId = ittId;
    });

    if (this.isEdit && this.ittId) {
      this.getITTDeveloperDataWithStep();
    }

    if (!this.isEdit) {
      this.tenderingService.setIttId(null);
    }
  }

  updateRFPorRFQFlag() {
    const currentRoute = this.router.url.toLowerCase(); // Get full URL path
    if(currentRoute.includes('rfp')){
      this.isRFPorRFQ = 'RFP';
    }else if(currentRoute.includes('rfq')){
      this.isRFPorRFQ = 'RFQ'
    }
    console.log('isRFPorRFQ:', this.isRFPorRFQ);
  }

  getITTDeveloperDataWithStep() {
    return this.tenderingService
      .ittDeveloperDataWithStep(this.projectId, this.ittId)
      .subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.handleITTDeveloperData(response?.data);
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
      });
  }

  handleITTDeveloperData(developerData) {
    this.currentITTStep = developerData.ittStep;
    this.ITTDeveloperData = developerData?.ittData || {};
  }

  nextStep() {
    if (this.currentITTStep < this.totalSteps) {
      this.currentITTStep++;

      // If RFP or RFQ, skip steps 7 (technical evaluation) and 8 (commercial evaluation)
      if (this.isRFPorRFQ === 'RFQ' && this.currentITTStep === 7) {
        this.closeModalEvent.emit(true);
        // this.currentITTStep = 9; // Jump past 7 & 8 to finish
      }
    }
  }

  previousStep() {
    if (this.currentITTStep > 1) {
      this.currentITTStep--;

      // If we are skipping steps and going backward, ensure we land on step 6
      if (this.isRFPorRFQ === 'RFQ' && this.currentITTStep === 8) {
        this.currentITTStep = 6;
      }
    }
  }

  handleCloseModal(closeModalValue: boolean) {
    if (closeModalValue) {
      this.closeModalEvent.emit(closeModalValue);
    }
  }

  handleWorkPackagesData(workPackagesEmittedData: any) {
    this.ITTDeveloperData['title'] = workPackagesEmittedData?.ittRecord?.title;
    this.ITTDeveloperData['work_packages_des'] = workPackagesEmittedData?.ittRecord?.work_packages_des;
    this.ITTDeveloperData['p_w_p'] = workPackagesEmittedData?.ittWPRecord;
    this.tenderingService.setIttId(workPackagesEmittedData?.ittRecord?.id);
  }

  handleCompaniesSelectionData(companiesSelectionEmittedData: any) {
    this.ITTDeveloperData['tendering_type'] = companiesSelectionEmittedData?.ittRecord?.tendering_type;
    this.ITTDeveloperData['receiverOrganizations'] = companiesSelectionEmittedData?.ittReceiverOrganizationRecord;
    this.tenderingService.setIttId(companiesSelectionEmittedData?.ittRecord?.id);
  }

  handleTenderSubmissionData(tenderSubmissionEmittedData: any) {
    this.ITTDeveloperData['submission_address'] = tenderSubmissionEmittedData?.submission_address;
    this.ITTDeveloperData['submission_deadline_date'] = new Date(tenderSubmissionEmittedData?.submission_deadline)
      .toISOString()
      .split('T')[0];
    this.tenderingService.setIttId(tenderSubmissionEmittedData?.id);
  }

  handlecontractualTermsData(contractualTermsEmittedData: any) {
    this.ITTDeveloperData['contract_type_details'] = contractualTermsEmittedData?.contract_type_details;
    this.ITTDeveloperData['contract_type_attachments'] = contractualTermsEmittedData?.contractual_terms_attachments;
    this.tenderingService.setIttId(contractualTermsEmittedData?.id);
  }

  handlekeyDatesData(keyDatesEmittedData: any) {
    this.ITTDeveloperData['issue_date'] = keyDatesEmittedData?.issue_date;
    this.ITTDeveloperData['clarification_deadline'] = keyDatesEmittedData?.clarification_deadline;
    this.ITTDeveloperData['contract_award_notification'] = keyDatesEmittedData?.contract_award_notification;
    this.ITTDeveloperData['contract_start_date'] = keyDatesEmittedData?.contract_start_date;
    this.ITTDeveloperData['project_completion_date'] = keyDatesEmittedData?.project_completion_date;
    this.tenderingService.setIttId(keyDatesEmittedData?.id);
  }

  handleTechnicalEvaluationData(technicalEvaluationDataEmittedData: any) {
    if (this.isRFPorRFQ === 'RFQ') return;
    this.ITTDeveloperData['technical_evaluation'] = technicalEvaluationDataEmittedData?.technical_evaluation_criteria;
    this.tenderingService.setIttId(technicalEvaluationDataEmittedData?.id);
  }

  handleCommercialEvaluationData(commercialEvaluationDataEmittedData: any) {
    if (this.isRFPorRFQ === 'RFQ') return;
    this.ITTDeveloperData['commercial_evaluation'] = commercialEvaluationDataEmittedData?.commercial_evaluation_criteria;
    this.tenderingService.setIttId(commercialEvaluationDataEmittedData?.id);
  }
}
